import React, { useEffect, useState } from 'react';

import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Checkbox } from '@buildoutinc/blueprint-react/ui/Checkbox';
import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { Label } from '@buildoutinc/blueprint-react/ui/Label';
import { Textarea } from '@buildoutinc/blueprint-react/ui/Textarea';

import { Button } from 'components/ui/button';
import { showSnackbar } from 'components/ui/snackbar/helper';

import { get, put } from 'helpers/api';

import type { BiographySettings, UserProfile } from 'components/users/types';

type Props = Pick<UserProfile, 'biography' | 'features' | 'paths'>;

export default function BiographyTab({ biography, features: { allowBioWikis }, paths }: Props) {
  const [settings, setSettings] = useState<BiographySettings>(biography);
  const [preview, setPreview] = useState('');
  const [saving, setSaving] = useState(false);

  const usingBioWiki = allowBioWikis && settings.useBioWiki;

  useEffect(() => {
    if (!usingBioWiki) return undefined;

    const handle = window.setTimeout(async () => {
      try {
        const html = await get(paths.markdownPreview, { params: { content: settings.bioWiki ?? '' } });
        setPreview(typeof html === 'string' ? html : '');
      } catch {
        // Preview is best-effort; leave the last rendered markup in place on failure.
      }
    }, 500);

    return () => window.clearTimeout(handle);
  }, [settings.bioWiki, usingBioWiki, paths.markdownPreview]);

  function handleChange(key: keyof BiographySettings, value: string | boolean) {
    setSettings(prevSettings => ({ ...prevSettings, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);

    try {
      await put(paths.update, { user: settings });
      showSnackbar({ text: 'Your biography was saved.', variant: 'success' });
    } catch {
      showSnackbar({ text: 'Something went wrong saving your biography.', variant: 'danger' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="form-vertical-field-container">
      {allowBioWikis && (
        <div className="d-flex align-items-center gap-2">
          <Checkbox
            checked={settings.useBioWiki}
            id="useBioWiki"
            onCheckedChange={checked => handleChange('useBioWiki', checked === true)}
          />
          <Label htmlFor="useBioWiki">Use Bio Wiki</Label>
        </div>
      )}

      {usingBioWiki ? (
        <>
          <Field className="form-group">
            <Field.Label htmlFor="bioWiki">Bio Wiki</Field.Label>
            <Textarea
              id="bioWiki"
              rows={6}
              value={settings.bioWiki ?? ''}
              onChange={e => handleChange('bioWiki', e.target.value)}
            />
            <Field.Description>
              View the{' '}
              <a href="http://en.wikipedia.org/wiki/Markdown#Example" rel="noreferrer" target="_blank">
                Syntax guide
              </a>
            </Field.Description>
          </Field>

          <div className="form-group">
            <div className="form-label">Preview</div>
            <Card>
              <Card.Body>
                <div dangerouslySetInnerHTML={{ __html: preview }} />
              </Card.Body>
            </Card>
          </div>
        </>
      ) : (
        <>
          <Field className="form-group">
            <Field.Label htmlFor="biography">Biography</Field.Label>
            <Textarea
              id="biography"
              rows={6}
              value={settings.biography ?? ''}
              onChange={e => handleChange('biography', e.target.value)}
            />
          </Field>

          <Field className="form-group">
            <Field.Label htmlFor="education">Education/Professional History</Field.Label>
            <Textarea
              id="education"
              rows={6}
              value={settings.education ?? ''}
              onChange={e => handleChange('education', e.target.value)}
            />
          </Field>

          <Field className="form-group">
            <Field.Label htmlFor="membershipsAndAssociations">Memberships and Associations</Field.Label>
            <Textarea
              id="membershipsAndAssociations"
              rows={6}
              value={settings.membershipsAndAssociations ?? ''}
              onChange={e => handleChange('membershipsAndAssociations', e.target.value)}
            />
          </Field>
        </>
      )}

      <div className="text-end">
        <Button disabled={saving} variant="primary" onClick={handleSave}>
          Save Biography
        </Button>
      </div>
    </div>
  );
}
